/**
 * src/main.js
 *
 * Entry point. Wires Firebase Auth state to the two top-level views:
 * signed-out → login card, signed-in → app shell (sidebar + routed content).
 * onAuthStateChanged is the single source of truth for which is rendered —
 * neither view navigates the other directly.
 */

import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './lib/firebase.js';
import { mountLogin } from './auth/login.js';
import { mountShell } from './shell/shell.js';
import { resetBootstrapCache } from './lib/session-store.js';
import { resetRouteListeners } from './lib/router.js';
import { applyDir } from './i18n/t.js';
import { getLang } from './i18n/store.js';
import { loadDict, warmDict } from './i18n/dicts.js';
import './styles/tokens.css';
import './styles/app.css';
import './i18n/rtl.css';

// Apply the persisted interface language + direction (LTR/RTL) BEFORE
// anything mounts, so both the login card and the app shell honor it from
// first paint — whichever view renders first (main.js is the single source
// of truth for which one mounts) never flashes the wrong direction.
// applyDir() (src/i18n/store.js's stampDocument, re-exported from t.js)
// reads whatever src/i18n/store.js already resolved from localStorage
// ('staffly.lang', default 'en' on first visit) and stamps <html lang/dir>
// to match. The toggle that changes it lives in the shell top bar
// (shell.js's wireLangToggle → setLang()), which re-stamps + notifies
// listeners on every change; this is just the boot-time read of whatever
// it last persisted.
applyDir();

const root = document.getElementById('app');

// Fetch the active language's strings BEFORE anything mounts.
//
// The dictionaries used to be static imports in t.js, which put both of them
// into the entry chunk: 1354 KB raw / 313 KB gzipped, of which ~1363 KB of
// source was en.js + ar.js. Every visitor paid the full download and parse
// cost of the language they would never see. They are dynamic imports now
// (src/i18n/dicts.js), so only the active one is fetched.
//
// The request starts at module evaluation — here — and is only awaited at the
// point of mounting, so it overlaps with Firebase's own auth-state round-trip
// rather than adding a serial hop in front of it. t() stays synchronous
// because nothing that calls it can run until this resolves.
const langReady = loadDict(getLang());

// Register the auth observer only once the strings are in memory, so every
// mount below stays fully SYNCHRONOUS and race-free. Deferring the mount
// itself instead would let two rapid auth transitions (sign-out immediately
// followed by sign-in) resolve out of order and mount the wrong view.
//
// This costs nothing: `auth` was already constructed at import time by
// lib/firebase.js and begins restoring persisted credentials then, not when
// an observer attaches — and Firebase replays the settled state to a late
// observer, so no transition is missed.
//
// On failure we register ANYWAY. A UI rendering raw dotted keys is ugly but
// navigable and self-evidently broken; a blank page is neither.
langReady.catch((err) => {
  console.error('[i18n] dictionary failed to load — mounting with raw keys', err);
}).then(() => {
  startAuthObserver();
  prefetchOtherLanguage();
});

/**
 * Pulls the OTHER language's dictionary in after the app is up, so the first
 * press of the language toggle is instant instead of costing a fetch.
 *
 * Deliberately conditional. Prefetching unconditionally would re-download the
 * ~100 KB gzipped dictionary this split just removed from the critical path —
 * deferring the cost rather than removing it. This is a bilingual product and
 * users really do flip language, so the prefetch is worth it on a connection
 * that can spare it and wrong on one that cannot:
 *
 *   - `saveData` is the user explicitly asking every site to send less. Honour it.
 *   - 2g/slow-2g means the prefetch would compete with the app's own reads.
 *
 * On either, we skip and let the toggle pay for itself — setLang() awaits the
 * load, so the flip is still correct, just not instant.
 *
 * Runs at idle so it never competes with first paint. requestIdleCallback is
 * absent on Safari <16.4, hence the timeout fallback.
 */
function prefetchOtherLanguage() {
  const conn = typeof navigator !== 'undefined' ? navigator.connection : null;
  if (conn && (conn.saveData || /^(slow-)?2g$/.test(conn.effectiveType || ''))) return;

  const other = getLang() === 'ar' ? 'en' : 'ar';
  const idle =
    typeof requestIdleCallback === 'function'
      ? requestIdleCallback
      : (fn) => setTimeout(fn, 2000);
  idle(() => warmDict(other));
}

function startAuthObserver() {
onAuthStateChanged(auth, (user) => {
  // P0 cross-account cache fix (2026-07-15, log-proven live bug):
  // session-store.js memoizes a successful getSessionBootstrap for the page
  // lifetime, and sign-out is SPA-internal (shell.js signOut(auth) — no
  // reload), so without this reset a sign-out → sign-in-as-someone-else in
  // the SAME tab handed the second account the FIRST account's bootstrap
  // (Cloud Logging showed zero getSessionBootstrap calls after the second
  // sign-in). Every auth-state transition — sign-in, sign-out, account
  // switch — starts from a cold bootstrap cache.
  resetBootstrapCache();
  // PERF/leak fix (2026-08-19): drop the outgoing shell's route-change
  // listeners with its DOM. Each shell mount registers one via
  // onRouteChange(); without this reset, every sign-out → sign-in cycle
  // (including admin ↔ employee persona switches, which each shell's own
  // per-mount unsub handle cannot see across) left one more listener
  // retaining a detached shell tree and firing on every route change.
  resetRouteListeners();
  root.innerHTML = '';
  if (user) {
    mountShell(root, user);
  } else {
    mountLogin(root);
  }
});
}
